import { useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { button } from './form.js';
import {
  ACCEPT_ATTRIBUTE,
  MAX_FILES,
  contentTypeOf,
  fileProblem,
  formatBytes,
  type MessageAttachment,
  type SupportApi,
} from '../lib/support.js';

/**
 * Files on a support request or reply (Jon, 2026-09-27).
 *
 * Each file uploads the moment it is chosen, straight to storage through a URL
 * support signs, so a file that is too big or the wrong kind says so before
 * anybody presses Send, and Send does not wait on a slow upload that could
 * have started a minute ago. What the form sends is the list of keys; support
 * attaches them to the message. An upload nobody sends is deleted by support's
 * retention sweep after a day.
 */
export interface PickedFile {
  id: number;
  name: string;
  size: number;
  state: 'uploading' | 'ready' | 'failed';
  key?: string;
  error?: string;
}

export function useAttachments(api: SupportApi) {
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const next = useRef(1);

  const update = (id: number, change: Partial<PickedFile>) =>
    setFiles((all) => all.map((f) => (f.id === id ? { ...f, ...change } : f)));

  async function upload(id: number, file: File, contentType: string) {
    try {
      const signed = await api.signUpload({ filename: file.name, contentType, bytes: file.size });
      await api.putFile(signed, file);
      update(id, { state: 'ready', key: signed.key });
    } catch (e) {
      update(id, { state: 'failed', error: e instanceof Error ? e.message : 'The file could not be uploaded.' });
    }
  }

  function add(chosen: FileList | File[]) {
    setProblem(null);
    const list = [...chosen];
    const room = MAX_FILES - files.length;
    if (list.length > room) {
      setProblem(`Up to ${MAX_FILES} files on one message.`);
    }
    for (const file of list.slice(0, Math.max(0, room))) {
      const refusal = fileProblem(file);
      if (refusal) {
        setProblem(refusal);
        continue;
      }
      const id = next.current++;
      setFiles((all) => [...all, { id, name: file.name, size: file.size, state: 'uploading' }]);
      void upload(id, file, contentTypeOf(file)!);
    }
  }

  return {
    files,
    problem,
    add,
    remove: (id: number) => setFiles((all) => all.filter((f) => f.id !== id)),
    reset: () => {
      setFiles([]);
      setProblem(null);
    },
    /** Keys of the files that made it; a failed one is left out, and said so. */
    keys: files.flatMap((f) => (f.state === 'ready' && f.key ? [f.key] : [])),
    uploading: files.some((f) => f.state === 'uploading'),
  };
}

export type Attachments = ReturnType<typeof useAttachments>;

export function AttachmentPicker({ attachments, disabled }: { attachments: Attachments; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const full = attachments.files.length >= MAX_FILES;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        aria-label="Attach files"
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) attachments.add(e.target.files);
          // Cleared, so choosing the same file again after removing it fires.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={disabled || full}
        onClick={() => input.current?.click()}
        style={{ ...button, marginTop: 0, fontSize: '.9rem', padding: '.4rem .8rem' }}
      >
        Attach files
      </button>
      <span style={hint}> Pictures or PDFs, up to 25 MB each.</span>

      {attachments.files.length > 0 && (
        <ul style={chips} aria-label="Attached files">
          {attachments.files.map((f) => (
            <li key={f.id} style={chip}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <span style={hint}>
                {f.state === 'uploading' ? 'Uploading…' : f.state === 'failed' ? 'Failed' : formatBytes(f.size)}
              </span>
              <button
                type="button"
                aria-label={`Remove ${f.name}`}
                onClick={() => attachments.remove(f.id)}
                disabled={disabled}
                style={removeButton}
              >
                ×
              </button>
              {f.error && <span style={{ ...hint, color: C.dangerText, flexBasis: '100%' }}>{f.error}</span>}
            </li>
          ))}
        </ul>
      )}
      {attachments.problem && (
        <p role="alert" style={{ margin: '.5rem 0 0', color: C.dangerText, fontSize: '.85rem' }}>
          {attachments.problem}
        </p>
      )}
    </div>
  );
}

/**
 * The files on one message, each opening in a new tab.
 *
 * The link is asked for on the click and lasts minutes, so it cannot be an
 * ordinary `href`. The tab is opened FIRST, inside the click, and pointed at
 * the file once the link arrives: a tab opened after an `await` is outside
 * the click and a popup blocker stops it.
 */
export function MessageFiles({
  files,
  open,
}: {
  files: MessageAttachment[];
  open: (id: string) => Promise<{ url: string }>;
}) {
  const [error, setError] = useState<string | null>(null);
  if (files.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', marginTop: '.6rem' }}>
      {files.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => {
            setError(null);
            const tab = window.open('', '_blank');
            open(f.id)
              .then(({ url }) => {
                if (tab) {
                  tab.opener = null;
                  tab.location.href = url;
                } else {
                  window.location.assign(url);
                }
              })
              .catch((e: unknown) => {
                tab?.close();
                setError(e instanceof Error ? e.message : 'That file could not be opened.');
              });
          }}
          style={{ ...button, marginTop: 0, fontSize: '.85rem', padding: '.3rem .7rem' }}
        >
          {f.filename} <span style={hint}>{formatBytes(f.bytes)}</span>
        </button>
      ))}
      {error && (
        <p role="alert" style={{ margin: 0, color: C.dangerText, fontSize: '.85rem', flexBasis: '100%' }}>
          {error}
        </p>
      )}
    </div>
  );
}

const hint: React.CSSProperties = { color: C.textMid, fontSize: '.85rem' };
const chips: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '.6rem 0 0', display: 'grid', gap: '.35rem' };
const chip: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '.6rem',
  padding: '.35rem .6rem',
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  fontSize: '.9rem',
};
const removeButton: React.CSSProperties = {
  marginLeft: 'auto',
  background: 'none',
  border: 'none',
  color: C.textMid,
  fontSize: '1.1rem',
  lineHeight: 1,
  cursor: 'pointer',
  padding: '0 .2rem',
};
